import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "../../api";
import { MARKETING } from "../../constants/testIds";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { GymLoader } from "../common/GymLoader";
import { AdPreview } from "./AdPreview";
import { CreativeEditor } from "./CreativeEditor";
import { useAppData } from "../../contexts/AppDataContext";
import { toast } from "../ui/sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../ui/dialog";

const rupees = (paise) => (paise == null ? "—" : `₹${(paise / 100).toLocaleString("en-IN")}`);

function today() {
  return new Date().toISOString().slice(0, 10);
}

// One campaign in detail: its measured performance, the creatives it will run, and the spend
// log that makes cost-per-lead a real number while ads are being run manually.
export function CampaignDetail({ campaignId, onOpenChange }) {
  const queryClient = useQueryClient();
  const { me } = useAppData();
  const gymName = me?.organization_display_name || "Your Gym";
  const [amount, setAmount] = useState("");
  const [spentOn, setSpentOn] = useState(today());
  const [editing, setEditing] = useState(false);
  const [draftCreatives, setDraftCreatives] = useState(null);

  const campaignQ = useQuery({
    queryKey: ["marketing-campaign", campaignId],
    queryFn: async () => (await api.get(`/marketing/campaigns/${campaignId}`)).data,
    enabled: !!campaignId,
  });

  const spendMutation = useMutation({
    mutationFn: () => api.post(`/marketing/campaigns/${campaignId}/spend`, {
      amount: Math.round(Number(amount) * 100), spent_on: spentOn,
    }),
    onSuccess: () => {
      toast.success("Spend recorded.");
      setAmount("");
      queryClient.invalidateQueries({ queryKey: ["marketing-campaign", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["marketing-campaigns"] });
      queryClient.invalidateQueries({ queryKey: ["marketing-analytics"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const saveCreativesMutation = useMutation({
    mutationFn: () => api.patch(`/marketing/campaigns/${campaignId}`, { creatives: draftCreatives }),
    onSuccess: () => {
      toast.success("Ads saved.");
      setEditing(false);
      setDraftCreatives(null);
      queryClient.invalidateQueries({ queryKey: ["marketing-campaign", campaignId] });
      queryClient.invalidateQueries({ queryKey: ["marketing-campaigns"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const c = campaignQ.data;

  return (
    <Dialog open={!!campaignId} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px] max-h-[85vh] overflow-y-auto">
        {campaignQ.isLoading || !c ? <GymLoader label="Loading campaign…" /> : (
          <>
            <DialogHeader>
              <DialogTitle>{c.name}</DialogTitle>
              <DialogDescription className="capitalize">
                {c.platform} · {c.objective.replace("_", " ")} · {rupees(c.daily_budget)}/day · {c.status}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-3 gap-3">
              <Stat label="Spend" value={rupees(c.metrics.spend)} />
              <Stat label="Leads" value={c.metrics.leads} />
              <Stat label="Cost / lead" value={rupees(c.metrics.cost_per_lead)} />
              <Stat label="Converted" value={c.metrics.converted} />
              <Stat label="Lead → member" value={c.metrics.lead_to_member_rate != null ? `${c.metrics.lead_to_member_rate}%` : "—"} />
              <Stat label="Cost / member" value={rupees(c.metrics.cost_per_conversion)} />
            </div>

            <div className="rounded-[12px] p-3" style={{ background: "var(--scarlet-accent-tint)" }}>
              <p className="text-xs font-semibold" style={{ color: "var(--scarlet-text)" }}>How leads reach this campaign</p>
              <p className="text-xs mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>
                Any lead saved with the source <code>{c.lead_source_tag}</code> counts here automatically, and still
                appears in your normal Leads pipeline.
              </p>
            </div>

            {c.audience_estimate && (
              <div>
                <p className="text-sm font-semibold mb-1.5" style={{ color: "var(--scarlet-text)" }}>Your first-party audience</p>
                <div className="grid grid-cols-2 gap-3">
                  <Stat label="Active members" value={c.audience_estimate.active_members} />
                  <Stat label="Open leads" value={c.audience_estimate.open_leads} />
                </div>
                {!c.audience_estimate.lookalike_ready && (
                  <p className="text-xs mt-2" style={{ color: "var(--scarlet-text-secondary)" }}>
                    Meta needs about {c.audience_estimate.lookalike_minimum} members before a lookalike audience
                    works well — you have {c.audience_estimate.active_members}.
                  </p>
                )}
              </div>
            )}

            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>
                  {c.status === "draft" ? "What you're about to publish" : "What's running"}
                </p>
                <Button
                  size="sm" variant="outline"
                  onClick={() => { setEditing(!editing); setDraftCreatives(editing ? null : (c.creatives || [])); }}
                >
                  {editing ? "Cancel" : "Edit ads & upload media"}
                </Button>
              </div>
              <p className="text-xs mb-2" style={{ color: "var(--scarlet-text-secondary)" }}>
                Exactly how this ad will appear once it goes live.
              </p>
              {editing ? (
                <CreativeEditor
                  creatives={draftCreatives || []} platform={c.platform} gymName={gymName}
                  onChange={setDraftCreatives}
                  onSave={() => saveCreativesMutation.mutate()}
                  saving={saveCreativesMutation.isPending}
                />
              ) : (
                <AdPreview creatives={c.creatives || []} platform={c.platform} gymName={gymName} />
              )}
            </div>

            <div>
              <p className="text-sm font-semibold mb-1.5" style={{ color: "var(--scarlet-text)" }}>Record ad spend</p>
              <p className="text-xs mb-2" style={{ color: "var(--scarlet-text-secondary)" }}>
                Log what you actually spent so cost-per-lead stays accurate while you run these ads yourself.
              </p>
              <div className="flex gap-2 items-end">
                <div className="space-y-1.5 flex-1">
                  <Label>Amount (₹)</Label>
                  <Input data-testid={MARKETING.spendAmountInput} type="number" min={0} value={amount} onChange={(e) => setAmount(e.target.value)} />
                </div>
                <div className="space-y-1.5 flex-1">
                  <Label>Date</Label>
                  <Input data-testid={MARKETING.spendDateInput} type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} />
                </div>
                <Button
                  data-testid={MARKETING.spendSubmit} disabled={!amount || Number(amount) <= 0 || spendMutation.isPending}
                  onClick={() => spendMutation.mutate()} className="text-white" style={{ background: "var(--scarlet-accent)" }}
                >
                  Add
                </Button>
              </div>
              {!!c.spend_entries?.length && (
                <div className="mt-2 space-y-1">
                  {c.spend_entries.map((s) => (
                    <div key={s.id} className="flex justify-between text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>
                      <span>{s.spent_on}</span><span className="tabular-nums">{rupees(s.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-[10px] p-2.5" style={{ border: "1px solid var(--scarlet-hairline)" }}>
      <p className="text-[11px]" style={{ color: "var(--scarlet-text-secondary)" }}>{label}</p>
      <p className="text-sm font-bold tabular-nums" style={{ color: "var(--scarlet-text)" }}>{value}</p>
    </div>
  );
}
